import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { Spinner, Gauge, ArrowsClockwise, Lightbulb } from '@phosphor-icons/react';

const scoreColor = (s) => {
  if (s >= 85) return '#34C759';
  if (s >= 70) return '#002FA7';
  if (s >= 55) return '#FF9500';
  return '#FF3B30';
};

const GRADE_DESC = {
  A: 'Court-ready record',
  B: 'Strong record',
  C: 'Developing record',
  D: 'Early-stage record',
  F: 'Insufficient record',
};

const OverallRing = ({ overall, grade }) => {
  const r = 52;
  const c = 2 * Math.PI * r;
  const color = scoreColor(overall);
  return (
    <div className="flex flex-col items-center gap-2 shrink-0">
      <div className="relative w-36 h-36" data-testid="scorecard-overall-ring">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r={r} fill="none" stroke="#F3F4F6" strokeWidth="10" />
          <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={c * (1 - (overall || 0) / 100)}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-outfit text-4xl font-bold" style={{ color }} data-testid="scorecard-overall-score">{overall}</span>
          <span className="text-[11px] font-ibm text-[#6B7280]">/ 100</span>
        </div>
      </div>
      <span className="text-sm font-ibm font-bold px-3 py-0.5 rounded-full border" data-testid="scorecard-grade"
        style={{ color, borderColor: color, backgroundColor: `${color}12` }}>
        Grade {grade} — {GRADE_DESC[grade] || ''}
      </span>
    </div>
  );
};

const ComponentRow = ({ comp }) => {
  const color = scoreColor(comp.score);
  return (
    <div className="py-3 border-b border-[#F3F4F6] last:border-0" data-testid={`scorecard-comp-${comp.key}`}>
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <span className="text-sm font-ibm font-semibold text-[#09090B]">{comp.label}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-ibm text-[#94A3B8]">weight {comp.weight}%</span>
          <span className="font-outfit text-sm font-bold w-8 text-right" style={{ color }} data-testid={`scorecard-comp-score-${comp.key}`}>{comp.score}</span>
        </div>
      </div>
      <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${comp.score}%`, backgroundColor: color, transition: 'width 0.6s ease' }} />
      </div>
      <p className="text-xs text-[#6B7280] font-ibm mt-1">{comp.detail}</p>
    </div>
  );
};

export const ScorecardTab = ({ caseId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/cases/${caseId}/scorecard`);
      setData(resp);
    } catch (err) {
      logger.error('failed to load scorecard:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center justify-center py-20" data-testid="scorecard-tab"><Spinner size={28} className="animate-spin text-[#002FA7]" /></div>;
  }
  if (!data) {
    return <div className="text-center py-14 text-[#6B7280] font-ibm" data-testid="scorecard-tab">Could not load the scorecard.</div>;
  }

  return (
    <div data-testid="scorecard-tab" className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2">
            <Gauge size={20} className="text-[#002FA7]" weight="fill" /> Investigation-Quality Scorecard
          </h3>
          <p className="text-sm text-[#6B7280] font-ibm mt-0.5 max-w-2xl">
            How complete and verifiable your case file currently is — computed live from your documents, timeline, claims, contradictions, open questions and issue tree.
          </p>
        </div>
        <button onClick={load} data-testid="scorecard-refresh-button"
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E7EB] text-[#09090B] font-ibm font-semibold rounded-md text-sm hover:bg-[#F3F4F6] transition-all shrink-0">
          <ArrowsClockwise size={14} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 flex flex-col md:flex-row gap-8 items-center md:items-start">
        <OverallRing overall={data.overall} grade={data.grade} />
        <div className="flex-1 w-full" data-testid="scorecard-components">
          {(data.components || []).map(c => <ComponentRow key={c.key} comp={c} />)}
        </div>
      </div>

      {(data.recommendations || []).length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="scorecard-recommendations">
          <h4 className="font-outfit text-base font-semibold text-[#09090B] flex items-center gap-2 mb-3">
            <Lightbulb size={18} className="text-[#FF9500]" weight="fill" /> How to strengthen this record
          </h4>
          <ul className="space-y-2">
            {data.recommendations.map((r, i) => (
              <li key={`rec-${i}`} className="flex items-start gap-2 text-sm font-ibm text-[#09090B]" data-testid={`scorecard-rec-${i}`}>
                <span className="w-5 h-5 rounded-full bg-[#002FA7]/10 text-[#002FA7] text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
